/**
 * AD-104 : barre de défilement horizontale EN HAUT des listes et en-tête de tableau collé.
 *
 * Même mécanisme que la tâche JC-520 du back-office FlowMap :
 *   1. une barre de défilement horizontale dessinée AU-DESSUS du tableau, celle du navigateur
 *      ne vivant qu'en bas, donc hors de vue sur une longue liste ;
 *   2. cette barre reste collée sous l'en-tête fixe de la page quand on descend ;
 *   3. l'en-tête du tableau (thead) reste visible lui aussi.
 *
 * La barre est dessinée (une piste et un curseur) et non confiée au navigateur : sa barre est
 * superposée sur la plupart des systèmes, donc invisible tant qu'on ne défile pas.
 *
 * Pour que l'en-tête se colle, le tableau doit défiler verticalement dans sa propre zone :
 * la carte « .table-card » défile déjà horizontalement, on borne donc sa hauteur à l'espace
 * disponible sous l'en-tête de la page, en réservant la place de ce qui la suit (pagination).
 *
 * Utilisation : const detacher = attacherDefilementHaut(carte) ; detacher() au démontage.
 */

const HAUTEUR_BARRE = 12
const POUCE_MINI = 40
const MARGE_BASSE = 16
const HAUTEUR_MINI = 260

// hauteur de l'en-tête fixe du back-office (.header-desktop, 60 px dans _header.scss)
function hauteurEnteteFixe() {
  const entete = document.querySelector('.header-desktop')
  const hauteur = entete ? Math.round(entete.getBoundingClientRect().height) : 0
  return hauteur > 0 ? hauteur : 60
}

// une cellule collée laisse voir les lignes au travers si son fond est transparent
function fondOpaque(depart) {
  let noeud = depart
  while (noeud && noeud !== document.documentElement) {
    const couleur = getComputedStyle(noeud).backgroundColor
    if (couleur && couleur !== 'transparent' && couleur.replace(/\s/g, '') !== 'rgba(0,0,0,0)') {
      return couleur
    }
    noeud = noeud.parentElement
  }
  return '#ffffff'
}

export function attacherDefilementHaut(carte) {
  if (!carte || carte.getAttribute('data-defilement-haut') === '1') {
    return () => {}
  }
  const tableau = carte.querySelector('table')
  if (!tableau) {
    return () => {}
  }
  carte.setAttribute('data-defilement-haut', '1')

  // ---- la piste et son curseur, posés juste avant la carte
  const piste = document.createElement('div')
  piste.className = 'defilement-haut'
  // position relative et non sticky : un conteneur « .main » en overflow:auto qui ne défile pas capte
  // le sticky, la barre suit donc l'en-tête de page par le calcul de suivre()
  piste.style.cssText = 'position:relative;z-index:2;width:100%;height:' + HAUTEUR_BARRE + 'px;margin:0 0 4px 0;'
    + 'background:#e2e5ea;border-radius:' + (HAUTEUR_BARRE / 2) + 'px;cursor:pointer;user-select:none;display:none;'
  const pouce = document.createElement('div')
  pouce.className = 'defilement-haut-pouce'
  pouce.style.cssText = 'position:absolute;top:0;left:0;height:' + HAUTEUR_BARRE + 'px;'
    + 'background:#9aa1ab;border-radius:' + (HAUTEUR_BARRE / 2) + 'px;cursor:grab;'
  pouce.addEventListener('mouseenter', () => { pouce.style.background = '#7d848e' })
  pouce.addEventListener('mouseleave', () => { pouce.style.background = '#9aa1ab' })
  piste.appendChild(pouce)
  carte.parentNode.insertBefore(piste, carte)

  // ---- l'en-tête du tableau se colle en haut de la zone de la carte
  carte.style.overflowY = 'auto'
  // AD-104 : la carte est une colonne flex, bornée en hauteur son corps rétrécit et le tableau
  // déborde sur le pied (pagination) ; on interdit ce rétrécissement et on colle le pied en bas
  Array.from(carte.children).forEach((enfant) => {
    enfant.style.flexShrink = '0' // AD-104
    if (enfant.classList.contains('card-footer')) {
      enfant.style.position = 'sticky' // AD-104
      enfant.style.bottom = '0' // AD-104
      enfant.style.left = '0' // AD-104 : reste visible quand le tableau défile à l'horizontale
      enfant.style.zIndex = '1' // AD-104
      enfant.style.backgroundColor = fondOpaque(enfant) // AD-104
    }
  })
  const entete = tableau.querySelector('thead')
  if (entete) {
    entete.style.position = 'sticky'
    entete.style.top = '-1px'
    entete.style.zIndex = '1'
    const fond = fondOpaque(entete.querySelector('th') || entete)
    entete.querySelectorAll('th').forEach((cellule) => {
      cellule.style.backgroundColor = fond
    })
  }

  const debord = () => carte.scrollWidth - carte.clientWidth

  // somme des défilements verticaux des ancêtres : la position de la carte page non défilée
  function defilementAncetres() {
    let total = 0
    let noeud = carte.parentElement
    while (noeud) {
      total += noeud.scrollTop || 0
      noeud = noeud.parentElement
    }
    return total
  }

  // hauteur de la carte bornée à l'espace visible, sous l'en-tête de page et la barre du haut
  function borner() {
    let apres = 0
    let suivant = carte.nextElementSibling
    while (suivant) {
      if (getComputedStyle(suivant).position !== 'fixed') {
        apres += suivant.getBoundingClientRect().height
      }
      suivant = suivant.nextElementSibling
    }
    // la carte tient dans l'écran sous ce qui la précède (filtres, barre du haut) : la page ne défile
    // plus, c'est le tableau qui défile, en-tête collé. Sur un petit écran le plancher HAUTEUR_MINI
    // laisse la page défiler, et suivre() garde alors la barre sous l'en-tête de page.
    const hautCarte = carte.getBoundingClientRect().top + defilementAncetres()
    let disponible = window.innerHeight - hautCarte - apres - MARGE_BASSE
    if (disponible < HAUTEUR_MINI) {
      disponible = HAUTEUR_MINI
    }
    carte.style.maxHeight = disponible + 'px'
  }

  // la barre reste visible sous l'en-tête fixe tant que la carte est à l'écran
  function suivre() {
    const rect = carte.getBoundingClientRect()
    const naturel = rect.top - HAUTEUR_BARRE - 4
    const limite = hauteurEnteteFixe()
    let decalage = 0
    if (naturel < limite && rect.bottom > limite + HAUTEUR_BARRE) {
      decalage = Math.min(limite - naturel, rect.height - HAUTEUR_BARRE)
    }
    piste.style.transform = decalage > 0 ? 'translateY(' + Math.round(decalage) + 'px)' : ''
  }

  function dessiner() {
    const reste = debord()
    piste.style.display = reste > 0 ? 'block' : 'none'
    if (reste <= 0) {
      return
    }
    const largeurPiste = piste.clientWidth
    const largeurPouce = Math.max(POUCE_MINI, Math.round(largeurPiste * carte.clientWidth / carte.scrollWidth))
    pouce.style.width = largeurPouce + 'px'
    pouce.style.left = Math.round((carte.scrollLeft / reste) * (largeurPiste - largeurPouce)) + 'px'
  }

  function rafraichir() {
    borner()
    dessiner()
    suivre()
  }

  function allerA(clientX, decalage) {
    const reste = debord()
    if (reste <= 0) {
      return
    }
    const rect = piste.getBoundingClientRect()
    const course = rect.width - pouce.offsetWidth
    if (course <= 0) {
      return
    }
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left - decalage) / course))
    carte.scrollLeft = ratio * reste
  }

  // ---- glisser le curseur, ou cliquer la piste pour y sauter
  let decalageSaisie = null
  const auClicPouce = (e) => {
    decalageSaisie = e.clientX - pouce.getBoundingClientRect().left
    pouce.style.cursor = 'grabbing'
    e.preventDefault()
    e.stopPropagation()
  }
  const auClicPiste = (e) => {
    allerA(e.clientX, pouce.offsetWidth / 2)
    decalageSaisie = pouce.offsetWidth / 2
    e.preventDefault()
  }
  const auMouvement = (e) => {
    if (decalageSaisie !== null) {
      allerA(e.clientX, decalageSaisie)
    }
  }
  const auRelachement = () => {
    if (decalageSaisie !== null) {
      decalageSaisie = null
      pouce.style.cursor = 'grab'
    }
  }
  pouce.addEventListener('mousedown', auClicPouce)
  piste.addEventListener('mousedown', auClicPiste)
  document.addEventListener('mousemove', auMouvement)
  document.addEventListener('mouseup', auRelachement)

  // la barre du bas et la barre du haut restent synchronisées
  carte.addEventListener('scroll', dessiner, { passive: true })
  window.addEventListener('resize', rafraichir)
  // défilement de la page, quel que soit l'élément qui le porte (body, .main)
  document.addEventListener('scroll', suivre, { passive: true, capture: true })

  // les données arrivent après le montage (chargement, pagination, filtres) : on se recale
  const observateur = new ResizeObserver(rafraichir)
  observateur.observe(tableau)
  observateur.observe(carte)
  // ce qui précède la carte peut changer de hauteur (badges de filtres) : on se recale aussi
  if (carte.parentElement) {
    observateur.observe(carte.parentElement)
  }

  rafraichir()

  return () => {
    observateur.disconnect()
    window.removeEventListener('resize', rafraichir)
    document.removeEventListener('scroll', suivre, { capture: true })
    document.removeEventListener('mousemove', auMouvement)
    document.removeEventListener('mouseup', auRelachement)
    carte.removeEventListener('scroll', dessiner)
    if (piste.parentNode) {
      piste.parentNode.removeChild(piste)
    }
    carte.removeAttribute('data-defilement-haut')
  }
}
